"use client";

import { useCallback, useState } from "react";

// Lazy-loaded AppKit instance
let appKitModal: { open: () => void; close: () => void } | null = null;
let initPromise: Promise<void> | null = null;

/**
 * Lazily initializes AppKit. Only loads the heavy @reown/appkit/react
 * bundle when actually needed (e.g., user clicks "Connect Wallet").
 */
export function ensureAppKit(): Promise<void> {
  if (appKitModal) return Promise.resolve();

  if (!initPromise) {
    initPromise = (async () => {
      const [
        { createAppKit },
        { bsc },
        { wagmiAdapter, projectId },
        { EXPECTED_CHAIN },
      ] = await Promise.all([
        import("@reown/appkit/react"),
        import("@reown/appkit/networks"),
        import("@/configs/wagmi"),
        import("@/configs/expected-chain"),
      ]);

      if (!projectId) {
        throw new Error("Project ID is not defined");
      }

      // Single source of truth for which chain the app expects -- previously
      // this file redefined its own copy of the fork chain (drifted out of
      // sync with expected-chain.ts, e.g. missing the fPOL nativeCurrency fix,
      // and used a looser "is NEXT_PUBLIC_RPC_URL set" check instead of the
      // authoritative NEXT_PUBLIC_CONTRACT_NET === "FORK" check).
      const defaultNetwork = EXPECTED_CHAIN;

      appKitModal = createAppKit({
        adapters: [wagmiAdapter],
        projectId,
        networks: [defaultNetwork, bsc] as [typeof defaultNetwork, typeof bsc],
        defaultNetwork,
        metadata: {
          name: "AranDao",
          description: "AranDao - NFT Marketplace",
          url: "https://arandao.com",
          icons: ["https://arandao.com/logo.svg"],
        },
        featuredWalletIds: [
          "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96",
          "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
        ],
        features: {
          analytics: true,
          email: false,
          swaps: false,
          socials: false,
        },
      });
    })();
  }

  return initPromise;
}

/**
 * Returns whether AppKit has been initialized.
 */
export function isAppKitReady(): boolean {
  return appKitModal !== null;
}

/**
 * Replacement for useAppKit() - lazily loads AppKit when open() is called.
 * The wallet modal only appears after the heavy bundle is loaded.
 */
export function useLazyAppKit() {
  const open = useCallback(async () => {
    await ensureAppKit();
    appKitModal?.open();
  }, []);

  const close = useCallback(() => {
    appKitModal?.close();
  }, []);

  return { open, close };
}

/**
 * Replacement for useAppKitState() - returns loading: false until AppKit loads.
 */
export function useLazyAppKitState() {
  const [loading] = useState(false);

  return { loading };
}
